import { SearchModes } from '@/domainLogic/index.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { calculateNights } from '@/app/components/search/utils/buildDealCards/utils.ts';
import {
  decorateMealPlan,
  decorateMealPlanMoreDescriptive,
  decorateNights,
  decorateNightsStay,
  decorateWaitingNightsStay,
} from '@/app/components/search/components/EvolutionCard/utils/getDecorators.ts';
import type {
  GetFeaturesInnerParams,
  GetFeaturesParams,
} from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import {
  getShortsDatesText,
  getShortsDatesWithMonthText,
} from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/geDatesText.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { parseApexFlexibleDays } from '@/app/common/apex/apexFlexibleDays.ts';
import { ORIGIN_TYPE } from '@/app/search/types.ts';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import { formatDateShortDayMonthYear } from '@/app/components/search/utils/dates/utilDates.ts';

const isDatesOnCardDifferent = (searchParams?: SearchParams, dateFromCard?: string, dateToCard?: string) => {
  const dateToUrl = searchParams?.[URLParameters.DATE_TO];
  const dateFromUrl = searchParams?.[URLParameters.DATE_FROM];

  return dateFromUrl !== dateFromCard || dateToUrl !== dateToCard;
};

export const getParams = ({
  commonData,
  localization,
  dealCard,
  t,
  promotionTypes,
  currency,
  searchParams,
  isWaiting,
  isLoading,
  isOverlay,
  curiosityParams,
  searchInfoData,
  searchInfoDetailData,
  hasPreselectedFlightLink,
  transportData,
  isClient,
  isMealFilterApplied,
  isApexResponse,
}: GetFeaturesParams): GetFeaturesInnerParams => {
  const locale = localization?.locale;
  const imageBasePath = localization?.imageBasePath;
  const searchMode = searchInfoData?.searchMode;
  const isLiveSearch = searchInfoData?.isLiveSearch;
  const isHO = searchMode === SearchModes.HO;
  const isDP = searchMode === SearchModes.DP;
  const isTH = searchMode === SearchModes.TH;
  const isMobile = commonData.isMobileDevice;
  const product = dealCard?.product;
  const soldOut = !!dealCard?.product?.dates?.soldOut;
  const alternative = dealCard?.product?.dates?.alternative;
  const cancellation = dealCard?.product?.rate?.features?.cancellation;
  const mealPlan = dealCard?.product?.rate?.features?.mealPlan;
  const accommodation = dealCard?.product?.accommodation;
  const isDPFlexible = isDP && !isLiveSearch;
  const opxFlexibleEnabled = localization?.configValuesApp?.opxFlexible;
  const flexibleAlternativeDates = localization?.configValuesApp?.flexibleAlternativeDatesEnabled;
  const showDescriptiveMealPlans = isHO || isDPFlexible;
  // MPH-1022: on an APEX search the card's flight is not propagated to the detail page, which
  // re-searches the whole airport group and can bind a different airport of it. Naming the group
  // instead of the card's own airport keeps the two pages from contradicting each other. Gated on
  // the apex response flag, like the MPH-945 switcher in ListContainer: apexEnabledForResults only
  // says the search was sent to apex, and apex may fall back to Vacation Catalog, whose cards do
  // carry their own airport to the detail page. Per search rather than per card
  // (product.rate.flags.apex) on purpose: mixing group and airport names down one list confuses.
  const isApexAirportGroupOrigin = isApexResponse && searchInfoDetailData?.origin?.type === ORIGIN_TYPE.AIRPORT_GROUP;
  const originName = isApexAirportGroupOrigin
    ? searchInfoDetailData?.origin?.description
    : dealCard?.product?.geo?.originName;
  const hasCustomCheckinCheckoutApplied = isDP && !!searchParams?.[URLParameters.CUSTOM_CHECKIN_CHECKOUT];
  const alternativeDeals = dealCard?.product?.dates?.alternativeDeals;
  // const hasOvernightFlight = searchParams?.[URLParameters.OVERNIGHT_FLIGHTS];
  // const showLoadingState = !!((hasCustomCheckinCheckoutEnabled && isWaiting) || (hasOvernightFlight && isWaiting));
  const cardCheckin = dealCard?.product?.dates?.checkin;
  const cardCheckout = dealCard?.product?.dates?.checkout;
  // const cardDateFrom = dealCard?.product?.dates?.dateFrom;
  // const cardDateTo = dealCard?.product?.dates?.dateTo;
  const nightsCount =
    dealCard?.product?.dates?.accNights ||
    dealCard?.product?.dates?.nights ||
    calculateNights(cardCheckin, cardCheckout);
  // const dateFrom = isWaiting ? searchParams?.[URLParameters.DATE_FROM] : cardDateFrom;
  // const dateTo = isWaiting ? searchParams?.[URLParameters.DATE_TO] : cardDateTo;
  const nightsText = isWaiting
    ? decorateWaitingNightsStay(searchParams?.[URLParameters.DATE_FROM], searchParams?.[URLParameters.DATE_TO], t)
    : decorateNights(nightsCount, t);
  const nightsTextStay = isWaiting
    ? decorateWaitingNightsStay(searchParams?.[URLParameters.DATE_FROM], searchParams?.[URLParameters.DATE_TO], t)
    : decorateNightsStay(nightsCount, t);
  const datesText = getShortsDatesText({ isWaiting, dealCard, localization, searchParams });
  const datesTextWithDay = getShortsDatesWithMonthText({ isWaiting, dealCard, localization, searchParams });
  // MPH-945: with a widened departure window every card's dates differ from the CSW, which would
  // otherwise hand most of the list to the OPX nearby-dates variant. The switcher owns the date line
  // while it is applied.
  // The context searchParams lag behind a window applied without a full search cycle (same reason the
  // meal filter is read from the live URL in Features/index.tsx), which would leave the date unstyled
  // on exactly the searches that need it. getSearchParamsClient falls back to the server params when
  // there is no DOM, so the server render agrees with the URL it was rendered for.
  const hasApexFlexibleDates =
    !!localization?.configValuesApp?.apexFlexibleDatesEnabled &&
    parseApexFlexibleDays(getSearchParamsClient(searchParams)?.[URLParameters.APEX_FLEXIBLE_DAYS]) > 0;
  // MPH-945: never offer per-card alternative dates while the switcher owns the dates. It cannot
  // happen today (the USP needs a non-live search, the switcher a live one), but the guard keeps the
  // two from ever stacking two nearby-dates mechanisms in the same list.
  const showFlexibleAlternativeDates = !!(
    flexibleAlternativeDates &&
    alternativeDeals &&
    !isLiveSearch &&
    !isWaiting &&
    !hasApexFlexibleDates
  );
  const meal = showDescriptiveMealPlans ? decorateMealPlanMoreDescriptive(t, mealPlan) : decorateMealPlan(t, mealPlan);
  const hasAlternativeDates = Array.isArray(alternative) && alternative.length > 0;
  const evolutionResultsOnlyEnabled = localization?.configValuesApp?.evolutionResultsOnlyEnabled || false;
  const isOpx = !!product?.rate?.flags?.opx;
  const isOpxFlexible = isOpx && opxFlexibleEnabled;
  const areDatesDifferentFromCSW = isDatesOnCardDifferent(searchParams, cardCheckin, cardCheckout);
  const areTripDatesDifferentFromCSW = areDatesDifferentFromCSW && isOpxFlexible;
  const areHotelDatesDifferentFromCSW = areDatesDifferentFromCSW && !isOpxFlexible;
  const hasDirectFlight = !!dealCard?.product?.transport?.direct;
  const isPartialStay = areHotelDatesDifferentFromCSW && isLiveSearch && hasCustomCheckinCheckoutApplied;
  const hasOpxFlexDates = areTripDatesDifferentFromCSW && isLiveSearch && opxFlexibleEnabled;
  const adultsChildrenCount = getAdultsChildrenCount(searchParams);
  const dateFrom = dealCard?.product?.dates?.dateFrom;
  const departureDate = dateFrom ? formatDateShortDayMonthYear(locale, dateFrom) : undefined;
  const checkinDate = cardCheckin ? formatDateShortDayMonthYear(locale, cardCheckin) : undefined;
  const isOvernight = transportData?.overnight?.is_overnight;

  return {
    accommodation,
    cancellation,
    commonData,
    curiosityParams,
    currency,
    datesText,
    datesTextWithDay,
    dealCard,
    soldOut,
    hasAlternativeDates,
    hasCustomCheckinCheckoutApplied,
    hasPreselectedFlightLink,
    imageBasePath,
    isClient,
    isHO,
    isDP,
    isTH,
    isOpx,
    isLiveSearch,
    isFlexible: !isLiveSearch,
    isLoading,
    isMobile,
    isOverlay,
    isWaiting,
    locale,
    localization,
    meal,
    mealPlan,
    nightsCount,
    nightsText,
    nightsTextStay,
    promotionTypes,
    searchInfoData,
    searchInfoDetailData,
    searchParams,
    showFlexibleAlternativeDates,
    t,
    transportData,
    isDPFlexible,
    areTripDatesDifferentFromCSW,
    areHotelDatesDifferentFromCSW,
    opxFlexibleEnabled,
    originName,
    hasDirectFlight,
    isPartialStay,
    hasOpxFlexDates,
    hasApexFlexibleDates,
    adultsChildrenCount,
    evolutionResultsOnlyEnabled,
    departureDate,
    checkinDate,
    isOvernight,
    isMealFilterApplied,
    isApexResponse,
  };
};
